import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  Tabs,
  ModalFooter,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import { useMobileContext } from "context/MobileContext";
import { useEffect, useRef, useState } from "react";
import {
  FaWallet,
  FaUser,
  FaTags,
  FaLock,
  FaPowerOff,
  FaUsers,
} from "react-icons/fa";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { Link, Outlet, useLocation } from "react-router-dom";
import { RootState } from "redux/store";

import "./ProfileLayout.css";
import { logoutUser } from "utils/auth/logout";

type LinkType = {
  path?: string;
  name?: JSX.Element;
  index: string;
  isSelected: boolean;
  isLogout?: boolean;
  icon?: JSX.Element;
};

const ProfileLayout = () => {
  const links: LinkType[] = [
    {
      path: "/profile/info",
      name: <FormattedMessage id="ProfileProfileText" />,
      index: "profile",
      isSelected: false,
      icon: <FaUser />,
    },
    {
      path: "/profile/balance",
      name: <FormattedMessage id="ProfileBalanceText" />,
      index: "balance",
      isSelected: false,
      icon: <FaWallet />,
    },
    {
      path: "/profile/subscriptions",
      name: <FormattedMessage id="ProfileSubscriptionsText" />,
      index: "subscriptions",
      isSelected: false,
      icon: <FaTags />,
    },
    {
      path: "/profile/affiliate",
      name: <>Affiliate</>,
      index: "affiliate",
      isSelected: false,
      icon: <FaUsers />,
    },
    // {
    //   path: '/profile/support',
    //   name: 'Support',
    //   index: 'support',
    //   isSelected: false,
    // },
    {
      path: "/profile/api",
      name: <FormattedMessage id="ProfileApiText" />,
      index: "api",
      icon: <FaLock />,
      isSelected: false,
    },
    {
      path: "/logout",
      name: <FormattedMessage id="ProfileLogoutText" />,
      index: "logout",
      isSelected: false,
      isLogout: true,
      icon: <FaPowerOff />,
    },
  ];
  const location = useLocation();
  const pathname = location.pathname;

  const [updatedLinks, setUpdatedLinks] = useState(links);
  const userState = useSelector((state: RootState) => state.userData);

  useEffect(() => {
    const newLinks = links.map((e: LinkType) => {
      if (e.path === pathname) {
        e.isSelected = true;
      } else e.isSelected = false;

      return e;
    });

    setUpdatedLinks(newLinks);
    // eslint-disable-next-line
  }, [pathname]);

  const { isMobile } = useMobileContext();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const initialRef = useRef(null);
  const finalRef = useRef(null);

  // Ends the server session, clears the local one, reloads to the home page.
  const onLogout = () => {
    void logoutUser();
  };

  const selectedView = updatedLinks.filter((link) => link.isSelected);

  // The mobile tab strip is driven by the route: uncontrolled, Chakra kept
  // tab 0 ("Profile") highlighted on every page. The strip scrolls sideways,
  // so the selected tab is also brought into view.
  const tabLinks = updatedLinks.filter((link) => link.index !== "logout");
  const selectedTab = tabLinks.findIndex((link) => link.isSelected);
  const tabListRef = useRef<HTMLDivElement>(null);
  // By position, not by aria-selected: Chakra marks the selected tab only after
  // its descendants register, which is later than this effect on first render.
  useEffect(() => {
    if (selectedTab < 0) return;
    tabListRef.current?.children[selectedTab]?.scrollIntoView?.({
      block: "nearest",
      inline: "nearest",
    });
  }, [selectedTab]);

  return (
    <>
      {userState.isUserLoggedIn && !isMobile ? (
        <div className="profile-layout">
          <nav className="profile-nav">
            <ul className="profile-nav-list">
              {updatedLinks.map((link) => (
                <li
                  key={link.index}
                  className={
                    link.isSelected
                      ? "selected"
                      : link.isLogout
                      ? "profile-logout"
                      : ""
                  }
                >
                  {
                    <>
                      {link.icon && link.icon}
                      {link.isLogout && link.isLogout ? (
                        <button onClick={onOpen} className="logout-btn">
                          {link.name}
                        </button>
                      ) : (
                        <Link
                          to={link.path}
                          aria-current={link.isSelected ? "page" : undefined}
                        >
                          {link.name}
                        </Link>
                      )}
                    </>
                  }
                </li>
              ))}
            </ul>
          </nav>
          <section
            className={`profile-layout-content ${selectedView[0]?.index}`}
          >
            <Outlet />
          </section>
        </div>
      ) : (
        <>
          <Tabs
            className="profile-mobile-tabs"
            index={selectedTab}
            onChange={() => undefined}
          >
            <TabList ref={tabListRef}>
              {tabLinks.map((link) => (
                <Tab key={link.index}>
                  <Link to={link.path}>{link.name}</Link>
                </Tab>
              ))}
            </TabList>
          </Tabs>
          <section className="profile-layout-content">
            <Outlet />
          </section>
        </>
      )}
      <Modal
        initialFocusRef={initialRef}
        finalFocusRef={finalRef}
        isOpen={isOpen}
        onClose={onClose}
        isCentered={false}
        size={"md"}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center" pb={0}>
            <FormattedMessage id="LogoutConfirmTitle" />
            <br />
            <FormattedMessage id="LogoutConfirmMessage" />
          </ModalHeader>

                      <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={onLogout}>
                <FormattedMessage id="LogoutButton" />
              </Button>
              <Button onClick={onClose}>
                <FormattedMessage id="CancelButton" />
              </Button>
            </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ProfileLayout;